import { RADashboardCardId } from "@/constants/ReportDashboard/cards-enums"
import { CardTitleLookup } from "@/constants/ReportDashboard/cards-metadata"
import { useTheme } from "@/contexts/RADashboardThemeContext/hooks"
import { RADashboardLayout } from "@/interfaces/raDashboard.interface"
import CloseIcon from "@mui/icons-material/Close"
import { Box, IconButton } from "@mui/material"
import React, { useMemo, useState } from "react"
import type { EventCallback, Layout } from "react-grid-layout"
import GridLayout, { useContainerWidth } from "react-grid-layout"
import { applyCardsOrderChange, applyResizeChange, cardsOrderToGridLayout, GUI_CANVAS_COLS, hasLayoutItemMoved, removeCardFromGridLayout } from "./DashboardGuiCanvasUtils"

type GuiEditorCanvasProps = {
  layout: RADashboardLayout | undefined
  isLocked: boolean
  setLayout: (newLayout: RADashboardLayout) => void
  selectedTab: string | null
}

const GuiEditorCanvas: React.FC<GuiEditorCanvasProps> = (props) => {
  const { layout, selectedTab, isLocked } = props
  const theme = useTheme()
  const { width: canvasWidth, mounted: canvasMounted, containerRef } = useContainerWidth({ measureBeforeMount: true })

  const cards = useMemo(
    () => (selectedTab ? layout?.tabLayouts[selectedTab]?.cardsOrder ?? [] : []),
    [layout, selectedTab]
  )

  // Key only on real card IDs so that adding/removing spacers doesn't remount the canvas
  const cardKey = cards.filter(id => !id.startsWith('empty_')).join(',')

  const gridUnit = canvasWidth / GUI_CANVAS_COLS

  const canvasStyle = {
    flex: 1,
    minHeight: 300,
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    p: 1,
    backgroundColor: theme.tabBackground,
    backgroundImage: `radial-gradient(circle, ${theme.textSecondary} 1px, transparent 1px)`,
    backgroundSize: `${gridUnit / 2}px ${gridUnit / 2}px`,
    overflow: 'hidden',
    pointerEvents: isLocked ? 'none' : 'auto',
  }

  return (
    <Box sx={canvasStyle} ref={containerRef}>
      {canvasMounted && cards.length > 0 && (
        <GridCanvas
          key={cardKey}
          width={canvasWidth}
          rowHeight={gridUnit}
          cards={cards}
          layout={layout!}
          selectedTab={selectedTab!}
          setLayout={props.setLayout}
        />
      )}
    </Box>
  )
}

type GridCanvasProps = {
  width: number
  rowHeight: number
  cards: (RADashboardCardId | string)[]
  layout: RADashboardLayout
  selectedTab: string
  setLayout: (newLayout: RADashboardLayout) => void
}

const GridCanvas: React.FC<GridCanvasProps> = ({ width, rowHeight, cards, layout, selectedTab, setLayout }) => {
  // Compute positions using the full cardsOrder (empties push real cards over),
  // then drop the empties — react-grid-layout only tracks draggable items
  const [gridLayout, setGridLayout] = useState<Layout>(() =>
    cardsOrderToGridLayout(cards, layout.cardSettings).filter(item => !item.i.startsWith('empty_'))
  )

  const handleLayoutChange = (newLayout: Layout) => {
    setGridLayout(currentLayout => {
      if (currentLayout.length !== newLayout.length) return newLayout
      if (!hasLayoutItemMoved(currentLayout, newLayout)) return currentLayout

      setLayout(applyCardsOrderChange(layout, selectedTab, newLayout))

      return newLayout
    })
  }

  // Native bottom-right resize. The grid suppresses onLayoutChange while a
  // resize is active and fires onResizeStop first with the final (compacted)
  // layout, so we own persistence here: store the new footprint as a custom
  // `custom_WxH` size. The follow-up onLayoutChange is a no-op because
  // gridLayout already matches the layout it passes back.
  const handleResizeStop: EventCallback = (newLayout, _oldItem, resizedItem) => {
    if (!resizedItem) return
    setGridLayout(newLayout)
    setLayout(applyResizeChange(layout, selectedTab, newLayout, resizedItem))
  }

  const handleRemoveCard = (cardId: string) => {
    const newGridLayout = removeCardFromGridLayout(gridLayout, cardId)
    setGridLayout(newGridLayout)
    setLayout(applyCardsOrderChange(layout, selectedTab, newGridLayout))
  }

  return (
    <GridLayout
      width={width}
      gridConfig={{ cols: GUI_CANVAS_COLS, rowHeight }}
      resizeConfig={{ enabled: true, handles: ['se'] }}
      layout={gridLayout}
      onLayoutChange={handleLayoutChange}
      onResizeStop={handleResizeStop}
    >
      {cards.filter(id => !id.startsWith('empty_')).map((cardId) => (
        <CardItem
          key={cardId}
          cardId={cardId}
          onRemoveCard={handleRemoveCard}
        />
      ))}
    </GridLayout>

  )
}

type CardItemProps = {
  cardId: string
  onRemoveCard: (cardId: string) => void
  children?: React.ReactNode
}

// react-grid-layout clones each grid child to inject its ref, className and
// positioning style (forwarded via ref + ...rest), and react-resizable injects
// the resize handle as the child's `children` prop — so we must render
// {children} for the handle to appear. The handle is styled below since the
// library's stylesheet isn't loaded in this app.
const CardItem = React.forwardRef<HTMLDivElement, CardItemProps>((props, ref) => {
  const { cardId, onRemoveCard, children, ...rest } = props
  const theme = useTheme()

  const cardStyle = {
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.cardBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab',
    height: '100%',
    color: theme.textPrimary,
    fontSize: 14,
    fontWeight: 500,
    userSelect: 'none',
    position: 'relative',
    textAlign: 'center',
    '& > .react-resizable-handle': {
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: 20,
      height: 20,
      cursor: 'se-resize',
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 3,
        bottom: 3,
        width: 6,
        height: 6,
        borderRight: `2px solid ${theme.textSecondary}`,
        borderBottom: `2px solid ${theme.textSecondary}`,
      },
    },
  }

  return (
    <Box ref={ref} sx={cardStyle} {...rest}>
      {CardTitleLookup[cardId as RADashboardCardId] ?? cardId}
      <IconButton
        size="small"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemoveCard(cardId) }}
        sx={{ position: 'absolute', top: 2, right: 2, color: theme.textSecondary }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      {children}
    </Box>
  )
})
CardItem.displayName = 'CardItem'

export default GuiEditorCanvas
